import { SchedulePosition, SchedulePositionLiveState } from "../interfaces/ISchedules";
import { getScheduleStatusStyles } from "./scheduleStatusStyles";
import { alpha, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { Timestamp } from "firebase/firestore";

export const getDate = (date: unknown): Date | null => {
  if (date == null) {
    return null;
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date;
  }

  if (typeof date === "object" && "toDate" in date && typeof (date as { toDate: () => Date }).toDate === "function") {
    try {
      const converted = (date as { toDate: () => Date }).toDate();
      if (Number.isNaN(converted.getTime())) {
        return null;
      }
      return converted;
    } catch {
      return null;
    }
  }

  if (typeof date === "object" && "seconds" in date && typeof (date as { seconds: number }).seconds === "number") {
    try {
      const raw = date as { seconds: number; nanoseconds?: number };
      const timestamp = Timestamp.fromMillis(raw.seconds * 1000 + (raw.nanoseconds || 0) / 1e6);
      const converted = timestamp.toDate();
      if (Number.isNaN(converted.getTime())) {
        return null;
      }
      return converted;
    } catch {
      return null;
    }
  }

  if (typeof date === "string" || typeof date === "number") {
    try {
      const converted = new Date(date);
      if (Number.isNaN(converted.getTime())) {
        return null;
      }
      return converted;
    } catch {
      return null;
    }
  }

  return null;
};

export const formatTime = (date: Date | null | undefined): string => {
  if (!date) {
    return "";
  }
  return date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
};

export const getRowStyle = (itemType: string, theme?: Theme): SystemStyleObject<Theme> => {
  if (!theme) {
    return {};
  }

  const styles: Record<string, SystemStyleObject<Theme>> = {
    ROUND: {},
    BREAK: {
      bgcolor: alpha(theme.palette.info.main, 0.08),
      "& .MuiTypography-root": {
        color: theme.palette.info.main,
        fontWeight: 600,
      },
    },
    CEREMONY: {
      bgcolor: alpha(theme.palette.secondary.main, 0.08),
      "& .MuiTypography-root": {
        color: theme.palette.secondary.main,
        fontWeight: 600,
      },
    },
    WARNING: {
      bgcolor: alpha(theme.palette.warning.main, 0.08),
      "& .MuiTypography-root": {
        color: theme.palette.warning.main,
        fontWeight: 600,
      },
    },
    INFO: {
      bgcolor: alpha(theme.palette.info.main, 0.08),
      "& .MuiTypography-root": {
        color: theme.palette.info.main,
        fontWeight: 600,
      },
    },
    OTHER: {},
  };

  return styles[itemType] || {};
};

export const getSchedulePositionStatus = (
  position: SchedulePosition | null | undefined,
): SchedulePositionLiveState => {
  if (!position) {
    return "PLANNED";
  }
  return position.liveState || "PLANNED";
};

export const hasPositionRounds = (position: SchedulePosition | null | undefined): boolean => {
  if (!position?.items) {
    return false;
  }
  return position.items.some(
    (item) => item.type === "ROUND" && item.competitionRoundId && item.competitionCategoryId,
  );
};

export const getSchedulePositionStyle = (
  position: SchedulePosition | null | undefined,
  theme?: Theme,
): SystemStyleObject<Theme> => {
  if (!position || !theme) {
    return {};
  }

  const status = getSchedulePositionStatus(position);
  return getScheduleStatusStyles(status, theme, { includeBackground: status === "FINISHED" });
};

export const getFloorPrefix = (floorLabel?: string): string => {
  const normalizedFloorLabel = floorLabel?.trim();
  if (!normalizedFloorLabel) {
    return "";
  }

  const parkietMatch = normalizedFloorLabel.match(/^Parkiet\s+(.+)$/i);
  if (parkietMatch?.[1]) {
    return parkietMatch[1].trim();
  }

  return normalizedFloorLabel;
};
